import React, { useState, useEffect, useRef } from 'react';
import { Volume2, VolumeX, Sparkles } from 'lucide-react';
import { isThemeSoundEnabled, setThemeSoundEnabled, playThemeSound } from '../../../utils/themeAudio';

const DIWALI_BLESSINGS = [
    '🪔 SHUBH DEEPAVALI! May divine light, prosperity & joy illuminate your path!',
    '✨ AUSPICIOUS BLESSINGS! May your inventory shine with golden victories!',
    '🪔 FESTIVAL OF LIGHTS! Fortune, wealth & success bless your gaming adventures!',
    '⭐ SACRED RADIANCE! May your year ahead be filled with triumphs!',
    '🎆 DIWALI DHAMAKA! May every battle bring you glory and celebration!'
];

const DiyaLampMascot = ({ onTriggerAnarFountain }) => {
    const [soundOn, setSoundOn] = useState(false);
    const [hovered, setHovered] = useState(false);
    const [blessingMessage, setBlessingMessage] = useState('');
    const [diyaFlares, setDiyaFlares] = useState(false);
    const blessingTimeoutRef = useRef(null);

    useEffect(() => {
        setSoundOn(isThemeSoundEnabled());
    }, []);

    const toggleSound = (e) => {
        e.stopPropagation();
        const next = !soundOn;
        setSoundOn(next);
        setThemeSoundEnabled(next);
        if (next) {
            playThemeSound('diwali', 'templeBell');
        }
    };

    const handleLightDiya = () => {
        setDiyaFlares(true);
        setTimeout(() => setDiyaFlares(false), 800);

        // Sound FX: Sacred temple bell + Sitar
        playThemeSound('diwali', 'templeBell');
        setTimeout(() => playThemeSound('diwali', 'sitar'), 250);
        setTimeout(() => playThemeSound('diwali', 'anar'), 500);

        // Trigger Anar firework fountain
        if (onTriggerAnarFountain) {
            onTriggerAnarFountain();
        }

        // Random Diwali Blessing
        const msg = DIWALI_BLESSINGS[Math.floor(Math.random() * DIWALI_BLESSINGS.length)];
        setBlessingMessage(msg);
        if (blessingTimeoutRef.current) clearTimeout(blessingTimeoutRef.current);
        blessingTimeoutRef.current = setTimeout(() => setBlessingMessage(''), 2500);
    };

    return (
        <>
            {/* Global Diwali Banner Popup - Perfectly Centered */}
            {blessingMessage && (
                <div className="fixed top-16 md:top-20 inset-x-0 flex justify-center z-[75] pointer-events-none px-4">
                    <div className="w-full max-w-md animate-bounce">
                        <div className="bg-gradient-to-r from-amber-600 via-red-600 to-amber-600 text-white font-bold text-center px-5 py-3.5 rounded-2xl shadow-2xl shadow-amber-950/80 border-2 border-yellow-300/80 backdrop-blur-md flex items-center justify-center gap-2">
                            <Sparkles className="w-5 h-5 text-yellow-200 animate-spin flex-shrink-0" />
                            <span className="text-sm tracking-wide text-shadow">{blessingMessage}</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Bottom-left Diya Mascot */}
            <div className="fixed bottom-5 left-4 md:left-8 z-[68] select-none flex flex-col items-start gap-2">
                {/* Speech Tooltip */}
                {hovered && !blessingMessage && (
                    <div className="bg-gray-950/95 text-amber-300 text-xs font-semibold px-3 py-1.5 rounded-xl border border-amber-500/50 shadow-lg shadow-black/60 animate-bounce whitespace-nowrap mb-1">
                        Light the Royal Diya! 🪔
                        <div className="absolute -bottom-1 left-4 w-2 h-2 bg-gray-950 border-b border-r border-amber-500/50 rotate-45" />
                    </div>
                )}

                <div className="flex items-center gap-2">
                    {/* The Interactive Clay Diya */}
                    <button
                        type="button"
                        onClick={handleLightDiya}
                        onMouseEnter={() => setHovered(true)}
                        onMouseLeave={() => setHovered(false)}
                        className={`group relative outline-none focus:outline-none transition-all duration-300 ${
                            diyaFlares ? 'animate-spooky-shake scale-125' : 'animate-float hover:scale-110'
                        }`}
                        title="Click to light the Royal Diya & receive Diwali blessings!"
                    >
                        {/* Golden Sacred Aura */}
                        <div className="absolute inset-0 rounded-full bg-amber-500/35 blur-xl group-hover:bg-yellow-400/55 transition-all duration-500 pointer-events-none animate-pulse" />

                        {/* Clay Diya SVG */}
                        <svg width="60" height="60" viewBox="0 0 100 100" className="relative drop-shadow-[0_6px_20px_rgba(245,158,11,0.85)] filter">
                            <defs>
                                <linearGradient id="diyaBrassGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="#f59e0b" />
                                    <stop offset="40%" stopColor="#d97706" />
                                    <stop offset="100%" stopColor="#78350f" />
                                </linearGradient>
                                <linearGradient id="diyaFlameGrad" x1="0%" y1="100%" x2="0%" y2="0%">
                                    <stop offset="0%" stopColor="#ea580c" />
                                    <stop offset="35%" stopColor="#f59e0b" />
                                    <stop offset="70%" stopColor="#fde047" />
                                    <stop offset="100%" stopColor="#ffffff" />
                                </linearGradient>
                                <radialGradient id="diyaGlow" cx="50%" cy="50%" r="50%">
                                    <stop offset="0%" stopColor="#fde047" stopOpacity="0.8" />
                                    <stop offset="50%" stopColor="#f59e0b" stopOpacity="0.3" />
                                    <stop offset="100%" stopColor="#f59e0b" stopOpacity="0" />
                                </radialGradient>
                            </defs>

                            {/* Flame Radiant Glow Halo */}
                            <circle cx="50" cy="30" r="22" fill="url(#diyaGlow)" className="animate-pulse" />

                            {/* Dancing Sacred Flame */}
                            <path
                                d="M 50 8 C 42 22 36 30 42 42 C 45 47 55 47 58 42 C 64 30 58 22 50 8 Z"
                                fill="url(#diyaFlameGrad)"
                                className="animate-diya-flicker"
                            />
                            {/* Pure White Core Flame */}
                            <path
                                d="M 50 20 C 46 28 44 34 47 40 C 49 43 51 43 53 40 C 56 34 54 28 50 20 Z"
                                fill="#ffffff"
                                opacity="0.95"
                            />

                            {/* Diya Scalloped Brass Pedestal */}
                            <path
                                d="M 32 82 Q 50 86 68 82 L 64 88 Q 50 92 36 88 Z"
                                fill="#92400e"
                                stroke="#78350f"
                                strokeWidth="1.5"
                            />

                            {/* Main Terracotta & Brass Bowl */}
                            <path
                                d="M 14 54 C 18 76 34 84 50 84 C 66 84 82 76 86 54 C 88 51 84 48 80 50 C 70 55 58 58 50 58 C 42 58 30 55 20 50 C 16 48 12 51 14 54 Z"
                                fill="url(#diyaBrassGrad)"
                                stroke="#78350f"
                                strokeWidth="1.5"
                            />

                            {/* Golden Oil Rim & Scalloped Ornaments */}
                            <ellipse cx="50" cy="53" rx="33" ry="8" fill="#b45309" stroke="#fbbf24" strokeWidth="1.5" />
                            <ellipse cx="50" cy="54" rx="27" ry="5.5" fill="#f59e0b" opacity="0.9" />

                            {/* Cotton Wick Spout */}
                            <path d="M 48 54 Q 50 48 50 44" stroke="#451a03" strokeWidth="3.5" strokeLinecap="round" />

                            {/* Golden Beads on Rim */}
                            <circle cx="26" cy="53" r="1.5" fill="#fef08a" />
                            <circle cx="38" cy="56" r="1.5" fill="#fef08a" />
                            <circle cx="50" cy="57" r="2" fill="#fef08a" />
                            <circle cx="62" cy="56" r="1.5" fill="#fef08a" />
                            <circle cx="74" cy="53" r="1.5" fill="#fef08a" />
                        </svg>
                    </button>

                    {/* Sound Toggle Pill */}
                    <button
                        type="button"
                        onClick={toggleSound}
                        className="px-2.5 py-1.5 rounded-full bg-gray-900/90 hover:bg-gray-800 text-amber-300 border border-amber-500/50 text-[11px] font-bold flex items-center gap-1.5 shadow-md shadow-black/40 transition-all hover:scale-105 active:scale-95"
                        title={soundOn ? 'Diwali Audio Enabled (Click to Mute)' : 'Enable Sacred Diwali Audio'}
                    >
                        {soundOn ? (
                            <>
                                <Volume2 className="w-3.5 h-3.5 text-amber-400 animate-pulse" />
                                <span className="hidden sm:inline">Audio ON</span>
                            </>
                        ) : (
                            <>
                                <VolumeX className="w-3.5 h-3.5 text-gray-400" />
                                <span className="hidden sm:inline text-gray-400">Audio OFF</span>
                            </>
                        )}
                    </button>
                </div>
            </div>
        </>
    );
};

export default DiyaLampMascot;
